/**
 * What the preview draws from: the brand's draft catalogue and its fonts.
 *
 * The catalogue is the preview feed (POST /admin/v1/preview-token, GET /v1/feed?preview=<token>),
 * fetched again after every save. Where the browser may not read that feed (it is served without
 * CORS headers for the console's origin), the same catalogue is built from the console's own items
 * (preview/feed.ts). The fonts are the brand's own files, served by the console (/brand-fonts),
 * loaded with the FontFace API under a preview-only name; never a network font service.
 */

import { useEffect, useState } from "react";
import { publishing, type Entity } from "@/api/admin";
import type { AdminClient, Brand } from "@/api/client";
import { fromEntities } from "./feed";
import type { FeedLike } from "./layout";

export interface PreviewData { feed: FeedLike; source: "feed" | "library"; leftOut: string[] }

/** Brands whose preview feed this browser may not read (no CORS for the console): asked once per visit. */
const unreadable = new Set<string>();

export function usePreviewData(client: AdminClient, catalogue: Entity[] | null, version: unknown): PreviewData | null {
  const [fetched, setFetched] = useState<FeedLike | null | "unavailable">(() => (unreadable.has(client.brand.id) ? "unavailable" : null));
  useEffect(() => {
    let live = true;
    if (unreadable.has(client.brand.id)) { setFetched("unavailable"); return; }
    (async () => {
      try {
        const { token } = await publishing.previewToken(client);
        const res = await fetch(`${client.brand.apiBase}/v1/feed?preview=${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error(String(res.status));
        const feed = (await res.json()) as FeedLike;
        if (live) setFetched(feed);
      } catch (e) {
        // A network error is the browser refusing the cross-origin read; a refused token is not.
        if (e instanceof TypeError) unreadable.add(client.brand.id);
        if (live) setFetched("unavailable");
      }
    })();
    return () => { live = false; };
  }, [client, version]);
  if (fetched && fetched !== "unavailable") return { feed: fetched, source: "feed", leftOut: [] };
  if (fetched === "unavailable" && catalogue) return { source: "library", ...fromEntities(catalogue) };
  return null;
}

const loaded = new Map<string, Promise<boolean>>();

/** The brand's display and body font families for CSS, each with the system font behind it. */
export function useBrandFonts(brand: Brand): { display: string; body: string } {
  const [ready, setReady] = useState(0);
  useEffect(() => {
    let live = true;
    for (const font of Object.values(brand.fonts ?? {})) {
      if (!font) continue;
      const name = `Preview ${font.family}`;
      if (!loaded.has(name)) {
        loaded.set(name, new FontFace(name, `url(${font.url})`, { weight: "100 900" }).load().then((face) => { document.fonts.add(face); return true; }).catch(() => false));
      }
      void loaded.get(name)!.then(() => { if (live) setReady((n) => n + 1); });
    }
    return () => { live = false; };
  }, [brand]);
  void ready;
  const family = (role: "display" | "body") => {
    const f = brand.fonts?.[role] ?? brand.fonts?.body;
    return f ? `"Preview ${f.family}", system-ui, sans-serif` : "system-ui, sans-serif";
  };
  return { display: family("display"), body: family("body") };
}
